/**
 * Location Agent - Intelligent Location Detection and Suggestion
 * 
 * This agent handles:
 * - GPS-based location detection
 * - Location suggestions based on user profile
 * - University-based location inference
 * - Context-aware location recommendations
 */

class LocationAgent {
  constructor() {
    this.locationCache = null;
    this.userProfile = null;
  }

  /**
   * Set user profile for context-aware suggestions
   */
  setUserProfile(profile) {
    this.userProfile = profile;
  }

  /**
   * Detect user's current location using GPS
   */
  async detectCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const location = await this.reverseGeocode(latitude, longitude);
            this.locationCache = location;
            resolve(location);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  /**
   * Reverse geocode coordinates to address
   */
  async reverseGeocode(lat, lon) {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'ClubFinder/1.0'
          }
        }
      );
      const data = await response.json();
      
      if (data && data.address) {
        return {
          state: data.address.state || '',
          city: data.address.city || data.address.town || data.address.village || '',
          area: data.address.suburb || data.address.neighbourhood || '',
          pincode: data.address.postcode || '',
          street: data.address.road || '',
          confidence: 0.9
        };
      }
      throw new Error('No address data');
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      throw error;
    }
  }

  /**
   * Suggest location based on user profile
   */
  suggestFromProfile() {
    if (!this.userProfile?.location) {
      return null;
    }

    return {
      state: this.userProfile.location.state || '',
      city: this.userProfile.location.city || '',
      area: this.userProfile.location.area || this.userProfile.location.street || '',
      pincode: this.userProfile.location.pincode || '',
      confidence: 0.8,
      source: 'profile'
    };
  }

  /**
   * Infer location from university selection
   */
  inferFromUniversity(universityName, universityLocations) {
    if (!universityName || !universityLocations[universityName]) {
      return null;
    }

    const location = universityLocations[universityName];
    return {
      state: location.state || '',
      city: location.city || '',
      area: '',
      pincode: '',
      confidence: 0.95,
      source: 'university'
    };
  }

  /**
   * Get intelligent location suggestions
   * Combines multiple sources for best recommendation
   */
  async getIntelligentLocation(options = {}) {
    const {
      useGPS = true,
      university = null,
      universityLocations = {}
    } = options;

    const suggestions = [];

    // 1. Try GPS detection
    if (useGPS) {
      try {
        const gpsLocation = await this.detectCurrentLocation();
        suggestions.push(gpsLocation);
      } catch (error) {
        console.log('GPS detection failed:', error.message);
      }
    }

    // 2. Check user profile
    const profileLocation = this.suggestFromProfile();
    if (profileLocation) {
      suggestions.push(profileLocation);
    }

    // 3. Check university selection
    if (university) {
      const uniLocation = this.inferFromUniversity(university, universityLocations);
      if (uniLocation) {
        suggestions.push(uniLocation);
      }
    }

    // 4. Return best suggestion (highest confidence)
    if (suggestions.length === 0) {
      return null;
    }

    // Sort by confidence and return best
    suggestions.sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
    return suggestions[0];
  }

  /**
   * Validate location completeness
   */
  validateLocation(location) {
    if (!location) return { valid: false, missing: ['all'] };
    
    const missing = [];
    if (!location.state) missing.push('state');
    if (!location.city) missing.push('city');
    
    return {
      valid: missing.length === 0,
      missing,
      location
    };
  }

  /**
   * Get cached location if available
   */
  getCachedLocation() {
    return this.locationCache;
  }
}

export default LocationAgent;

